import { useEffect } from 'react';

type WakeLockSentinelLike = {
  released: boolean;
  release: () => Promise<void>;
  addEventListener: (type: 'release', listener: () => void) => void;
};

type WakeLockCapableNavigator = Navigator & {
  wakeLock?: { request: (type: 'screen') => Promise<WakeLockSentinelLike> };
};

/**
 * Holds a screen wake lock so an always-on store display never sleeps.
 *
 * The lock is dropped by the browser whenever the page is hidden (or the device
 * is locked), so it has to be re-acquired on every return to visibility —
 * acquiring it once at mount is not enough for a device that runs for weeks.
 *
 * Silently does nothing where the API is unavailable; the caller doesn't need
 * to care, and a missing wake lock is never worth breaking the page over.
 */
export function useKeepAwake(enabled = true) {
  useEffect(() => {
    if (!enabled) return;

    const nav = navigator as WakeLockCapableNavigator;
    if (!nav.wakeLock) return;

    let sentinel: WakeLockSentinelLike | null = null;
    let cancelled = false;

    const acquire = async () => {
      if (cancelled || document.visibilityState !== 'visible') return;
      if (sentinel && !sentinel.released) return;

      try {
        sentinel = await nav.wakeLock!.request('screen');
        // Re-acquire if the system takes it back for any reason.
        sentinel.addEventListener('release', () => {
          if (!cancelled) void acquire();
        });
      } catch (error) {
        // Denied (battery saver, permissions policy) — not fatal.
        console.warn('Screen wake lock unavailable:', error);
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') void acquire();
    };

    void acquire();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      void sentinel?.release().catch(() => undefined);
      sentinel = null;
    };
  }, [enabled]);
}
